// Archivo para eñ wizard de consumidores
// Archivo para el wizard de consumidores con mejoras implementadas

import React, { useEffect, useMemo, useState } from 'react'
import {
  Box,
  Button,
  Container,
  Stepper,
  Step,
  StepLabel,
  Typography,
  MenuItem,
  TextField,
  CircularProgress,
  Snackbar,
  Alert,
  Paper,
  Grid,
} from '@mui/material'
import { me } from '../../services/api'
import { getLayoutsByProduct } from '../../services/layoutService'
import { getActiveLayout, parseLayoutConfig } from '../../utils/layoutUtils'
import {
  getClients,
  getProductsByClient,
  createConsumer,
} from '../../services/ConsumerServices'

const steps = [
  'Seleccionar Producto',
  'Llenar Datos del Consumidor',
  'Confirmación',
]

const ConsumerWizard = () => {
  const [activeStep, setActiveStep] = useState(0)

  const [isGlobalAdmin, setIsGlobalAdmin] = useState(false)

  const [clients, setClients] = useState([])
  const [selectedClient, setSelectedClient] = useState('')
  const [loadingClients, setLoadingClients] = useState(false)

  const [products, setProducts] = useState([])
  const [loadingProducts, setLoadingProducts] = useState(false)
  const [selectedProduct, setSelectedProduct] = useState('')

  const [layoutConfig, setLayoutConfig] = useState([])

  const [formData, setFormData] = useState({
    full_name: '',
    phone: '',
    email: '',
    custom_fields: {},
  })

  const [loading, setLoading] = useState(false)
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info',
  })

  // Determinar rol del usuario (globalAdmin) desde /auth/me
  useEffect(() => {
    const bootstrap = async () => {
      try {
        const user = await me()
        const roles = user?.roles || []
        const isGlobal = Array.isArray(roles)
          ? roles.some((r) => {
              const type = typeof r === 'string' ? r : r?.type
              const name = typeof r === 'string' ? r : r?.name_role
              return type === 'globalAdmin' || name === 'globalAdmin'
            })
          : false
        setIsGlobalAdmin(Boolean(isGlobal))
      } catch (err) {
        console.error('[Wizard] No se pudo obtener /auth/me:', err)
        // Si falla, asumimos NO globalAdmin para evitar exponer clientes
        setIsGlobalAdmin(false)
      }
    }
    bootstrap()
  }, [])

  // Si es globalAdmin: cargar lista de clientes
  useEffect(() => {
    const fetchClients = async () => {
      if (!isGlobalAdmin) return
      setLoadingClients(true)
      try {
        const res = await getClients()
        setClients(Array.isArray(res) ? res : [])
      } catch (error) {
        console.error('[Wizard] Error al cargar clientes:', error)
        setClients([])
        setSnackbar({
          open: true,
          message: 'Error al cargar clientes.',
          severity: 'error',
        })
      } finally {
        setLoadingClients(false)
      }
    }
    fetchClients()
  }, [isGlobalAdmin])

  // Cargar productos:
  // - Si NO es globalAdmin: al montar (scoped al token)
  // - Si es globalAdmin: cuando seleccione cliente
  useEffect(() => {
    const fetchProducts = async () => {
      setLoadingProducts(true)
      try {
        if (isGlobalAdmin) {
          if (!selectedClient) {
            setProducts([])
            return
          }
          const res = await getProductsByClient(selectedClient)
          setProducts(Array.isArray(res) ? res : [])
          return
        }

        // No globalAdmin: usa clientId del token (param 0 por compat)
        const res = await getProductsByClient()
        setProducts(Array.isArray(res) ? res : [])
      } catch (error) {
        console.error('[Wizard] Error al cargar productos:', error)
        setProducts([])
        setSnackbar({
          open: true,
          message: 'Error al cargar productos disponibles.',
          severity: 'error',
        })
      } finally {
        setLoadingProducts(false)
      }
    }

    // Condiciones para ejecutar:
    // - no globalAdmin: ejecuta cuando isGlobalAdmin está definido
    // - globalAdmin: ejecuta cuando cambie selectedClient
    if (isGlobalAdmin) {
      fetchProducts()
    } else {
      // evita correr antes de saber si es globalAdmin o no
      // (si isGlobalAdmin inicial false y luego true, esto se reevalúa)
      fetchProducts()
    }
  }, [isGlobalAdmin, selectedClient])

  // Si cambia cliente, resetear producto y layout
  useEffect(() => {
    setSelectedProduct('')
    setLayoutConfig([])
    setFormData((prev) => ({ ...prev, custom_fields: {} }))
  }, [selectedClient])

  // Si cambia producto, resetear layout
  useEffect(() => {
    setLayoutConfig([])
    setFormData((prev) => ({ ...prev, custom_fields: {} }))
  }, [selectedProduct])

  const fetchLayout = async (productId) => {
    const cacheKey = `layout_${productId}`
    const cached = sessionStorage.getItem(cacheKey)
    if (cached) {
      setLayoutConfig(JSON.parse(cached))
      return
    }

    try {
      const layoutData = await getLayoutsByProduct(productId)
      const payload = layoutData?.data ?? layoutData
      const layouts = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.layouts)
          ? payload.layouts
          : payload
            ? [payload]
            : []

      if (layouts.length === 0) {
        setSnackbar({
          open: true,
          message: 'Este producto no tiene layout aplicado.',
          severity: 'warning',
        })
        setLayoutConfig([])
        return
      }

      const activeLayout = getActiveLayout(layouts)
      const activeLayoutConfig = parseLayoutConfig(
        activeLayout?.config ?? activeLayout?.layoutConfig ?? activeLayout?.fields
      )

      if (!Array.isArray(activeLayoutConfig) || activeLayoutConfig.length === 0) {
        setSnackbar({
          open: true,
          message: 'Layout inválido o sin configuración.',
          severity: 'error',
        })
        setLayoutConfig([])
        return
      }

      sessionStorage.setItem(cacheKey, JSON.stringify(activeLayoutConfig))
      setLayoutConfig(activeLayoutConfig)
    } catch (err) {
      console.error('[Wizard] Error al obtener layout:', err)
      setSnackbar({
        open: true,
        message: 'Error al obtener layout.',
        severity: 'error',
      })
      setLayoutConfig([])
    }
  }

  const handleNext = async () => {
    if (activeStep === 0) {
      if (isGlobalAdmin && !selectedClient) {
        setSnackbar({
          open: true,
          message: 'Selecciona un cliente.',
          severity: 'warning',
        })
        return
      }

      if (!selectedProduct) {
        setSnackbar({
          open: true,
          message: 'Selecciona un producto.',
          severity: 'warning',
        })
        return
      }

      await fetchLayout(selectedProduct)
    }

    setActiveStep((prev) => prev + 1)
  }

  const handleBack = () => setActiveStep((prev) => prev - 1)

  const handleChange = (field, value, isCustom = false) => {
    if (isCustom) {
      setFormData((prev) => ({
        ...prev,
        custom_fields: {
          ...prev.custom_fields,
          [field]: value,
        },
      }))
      return
    }

    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }))
  }

  const handleSubmit = async () => {
    setLoading(true)
    try {
      const payload = {
        ...formData,
        product_id: selectedProduct,
        // Nota: NO enviamos client_id: el backend debe derivarlo del producto/token según rol
      }

      if (!payload.full_name || payload.full_name === 'Sin nombre') {
        setSnackbar({
          open: true,
          message: 'El nombre completo es requerido',
          severity: 'error',
        })
        return
      }

      await createConsumer(payload)
      setSnackbar({
        open: true,
        message: 'Consumidor creado exitosamente.',
        severity: 'success',
      })
      setTimeout(() => {
        setActiveStep(0)
        // opcional: reset parcial
        setFormData({
          full_name: '',
          phone: '',
          email: '',
          custom_fields: {},
        })
      }, 1200)
    } catch (err) {
      setSnackbar({
        open: true,
        message:
          err?.response?.data?.error ||
          err?.response?.data?.message ||
          'Error al crear consumidor.',
        severity: 'error',
      })
    } finally {
      setLoading(false)
    }
  }

  const nextDisabled = useMemo(() => {
    if (loadingClients || loadingProducts) return true
    if (activeStep !== 0) return false
    if (isGlobalAdmin && !selectedClient) return true
    if (!selectedProduct) return true
    return false
  }, [
    activeStep,
    isGlobalAdmin,
    selectedClient,
    selectedProduct,
    loadingClients,
    loadingProducts,
  ])

  return (
    <Container sx={{ mt: 4 }}>
      <Typography variant="h4" gutterBottom>
        Crear Consumidor
      </Typography>

      <Stepper activeStep={activeStep} sx={{ my: 4 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {activeStep === 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {isGlobalAdmin && (
            <>
              {loadingClients ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <TextField
                  select
                  fullWidth
                  label="Selecciona un cliente"
                  value={selectedClient}
                  onChange={(e) => setSelectedClient(e.target.value)}
                  disabled={clients.length === 0}
                >
                  {clients.map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      {c.name_client || c.name || `Cliente ${c.id}`}
                    </MenuItem>
                  ))}
                </TextField>
              )}

              {clients.length === 0 && !loadingClients && (
                <Alert severity="warning">
                  No hay clientes disponibles para tu cuenta o no tienes
                  permisos.
                </Alert>
              )}
            </>
          )}

          {loadingProducts ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <TextField
                select
                fullWidth
                label="Selecciona un producto"
                value={selectedProduct}
                onChange={(e) => setSelectedProduct(e.target.value)}
                disabled={
                  products.length === 0 || (isGlobalAdmin && !selectedClient)
                }
              >
                {products.map((p) => (
                  <MenuItem key={p.id} value={p.id}>
                    {p.name_product}
                  </MenuItem>
                ))}
              </TextField>

              {products.length === 0 && !loadingProducts && (
                <Alert severity="warning">
                  No hay productos para el cliente seleccionado.
                </Alert>
              )}
            </>
          )}
        </Box>
      )}

      {activeStep === 1 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="Nombre completo"
            fullWidth
            value={formData.full_name}
            onChange={(e) => handleChange('full_name', e.target.value)}
          />
          <TextField
            label="Teléfono"
            fullWidth
            value={formData.phone}
            onChange={(e) => handleChange('phone', e.target.value)}
          />
          <TextField
            label="Correo electrónico"
            fullWidth
            value={formData.email}
            onChange={(e) => handleChange('email', e.target.value)}
          />

          {Array.isArray(layoutConfig) &&
            layoutConfig.map((field) => (
              <TextField
                key={field.name}
                label={field.label}
                fullWidth
                value={formData.custom_fields[field.name] || ''}
                onChange={(e) => handleChange(field.name, e.target.value, true)}
              />
            ))}
        </Box>
      )}

      {activeStep === 2 && (
        <Paper sx={{ p: 3, mt: 2 }}>
          <Typography variant="h6" gutterBottom>
            Confirmación de Datos
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography>
                <strong>Nombre completo:</strong> {formData.full_name}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography>
                <strong>Teléfono:</strong> {formData.phone}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography>
                <strong>Correo electrónico:</strong> {formData.email}
              </Typography>
            </Grid>

            {Array.isArray(layoutConfig) &&
              layoutConfig.map((field) => (
                <Grid item xs={12} sm={6} key={field.name}>
                  <Typography>
                    <strong>{field.label}:</strong>{' '}
                    {formData.custom_fields[field.name] || '-'}
                  </Typography>
                </Grid>
              ))}
          </Grid>
        </Paper>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
        <Button disabled={activeStep === 0} onClick={handleBack}>
          Atrás
        </Button>

        {activeStep < steps.length - 1 ? (
          <Button onClick={handleNext} disabled={nextDisabled}>
            Siguiente
          </Button>
        ) : (
          <Button variant="contained" onClick={handleSubmit} disabled={loading}>
            {loading ? <CircularProgress size={24} /> : 'Crear consumidor'}
          </Button>
        )}
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Container>
  )
}

export default ConsumerWizard
